import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import * as theme from '../../theme';
import Heading from '../../components/Heading';
import TruCard from '../../components/TruCard';
import CreditCardList from '../../components/dashboards/lists/CreditCardList';
import ResourceItem from '../../components/dashboards/items/ResourceItem';
import ConsentModal from '../../components/ConsentModal'; // Import the new modal
import dayjs from 'dayjs';
import TransactionHistoryList from '../../components/dashboards/lists/TransactionHistoryList';

export default function Billing() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [practitioner, setPractitioner] = useState({});
  const [billing, setBilling] = useState({ cards: [] });
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false); // State for modal
  const { id } = useParams();

  useEffect(() => {
    if (id != null) {
      getPractitioner();
    }
  }, [id]);

  async function getPractitioner() {
    try {
      const docRef = doc(db, "practitioners", id);
      const docSnap = await getDoc(docRef);
      setPractitioner(docSnap.data() || {});
    } catch (err) {
      console.log("Failed to get practitioner: " + err.message);
    }

    try {
      const billingRef = doc(db, "billing", id);
      const billingSnap = await getDoc(billingRef);
      setBilling(billingSnap.data() || { cards: [] });
    } catch (err) {
      console.log("Failed to get billing details for practitioner: " + err.message);
    }

    try {
      const transCollectionRef = collection(db, `practitioners/${id}/transactions`);
      const querySnapshot = await getDocs(transCollectionRef);
      let allTransactions = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data && data.trans) {
          allTransactions = [...allTransactions, ...data.trans];
        }
      });
      // Sort transactions by dateCreated descending
      allTransactions.sort((a, b) => b.dateCreated.toMillis() - a.dateCreated.toMillis());
      setTransactions(allTransactions);
    } catch (err) {
      console.log("Failed to get transactions for practitioner: " + err.message);
    }

    try {
      const clientsRef = doc(db, "practitioners", id, "clients", "list");
      const clientsSnap = await getDoc(clientsRef);
      setClients(clientsSnap.data()?.clients || []);
    } catch (err) {
      console.log("Failed to get clients for practitioner: " + err.message);
    }
  }

  // Handle opening the consent modal
  const handleOpenConsentModal = () => {
    setIsConsentModalOpen(true);
  };

  // Handle closing the consent modal
  const handleCloseConsentModal = () => {
    setIsConsentModalOpen(false);
  };

  if (!practitioner || !billing) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Billing</div>
        <div>Loading...</div>
      </div>
    );
  }

  // Determine practitioner name for the consent form
  const practitionerName = practitioner.businessName || `${practitioner.firstName} ${practitioner.lastName}`.trim();

  return (
    <div style={theme.Container.flexColumnContainer}>
      <div style={{ ...theme.Container.flexColumn, marginBottom: 10 }}>
        <Heading
          title={`Billing`}
          onClick={() => currentUser.role === 'practitioner' ? navigate(`/dashboard`) : navigate(`/practitioners/${id}`)} 
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 20 }}>
            <CreditCardList practitionerId={id} billing={billing} currentUser={currentUser} getPractitioner={getPractitioner} />
            <TruCard title={'Contract Agreement'}>
              {practitioner !== null && practitioner.consent?.agreed &&
                <ResourceItem 
                  icon={'complete'} 
                  label={'Agreed on ' + dayjs(practitioner.consent.dateSigned.toDate()).format('MMMM DD, YYYY')} 
                  actionLink={handleOpenConsentModal} // Trigger modal
                />
              }
            </TruCard>
          </div>
        </div>
        <div style={theme.Container.flexTwo}>
          <div style={{ ...theme.Container.flexRow, width: '100%', gap: 20 }}>
            <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 20 }}>
              <TruCard title={'Transaction History'} style={{ width: '100%' }}>
                <TransactionHistoryList transactions={transactions} clients={clients} practitioner={practitioner} />
              </TruCard>
            </div>
          </div>
        </div>
      </div>
      {/* Render the Consent Modal */}
      <ConsentModal 
        open={isConsentModalOpen} 
        onClose={handleCloseConsentModal} 
        practitionerName={practitionerName}
        consentVersion={practitioner.consent?.version || 'Unknown'}
        dateSigned={dayjs(practitioner.consent?.dateSigned.toDate()).format('MMMM DD, YYYY')}
      />
    </div>
  );
}